import React from 'react';
import {Message, getMessage} from 'epic-diesel-common';
import set from 'lodash.set';
import Countries from 'epic-diesel-form/src/constants/Countries';
import FormsyForm from 'epic-diesel-form/src/components/external/FormsyForm';
import {autoFillForm} from '../../../utils/SignupUtils';
import {isValidChannel, isValidSocialChannels} from '../../../utils/ValidationUtils';
import {SocialChannel, Eula, GuardianInfo, ErrorCode} from '../../../../shared/types';
import {LoadIndicator} from '../..';
import {WithApplicationViewProps} from '../../../hocs/WithApplicationView';
import withTalon, {WithTalonProps} from '../../../hocs/withTalon';

//@ts-ignore
import styles from './ApplicationForm.pcss';

export interface ApplicationFormProps extends WithApplicationViewProps {
    disclaimerEnabled?: boolean;
    children: React.ComponentType<any>;
}

export interface Form {
    channels: SocialChannel[];
    mainChannelPlatform: string;
    age18Acknowledgment: boolean;
    guardianAcknowledgment: boolean;
    guardianInfo: GuardianInfo;
    eulaAccepted: boolean;
    guardianAccepted: boolean;
    agreementAccepted: boolean;
    disclaimerAccepted: boolean;
    country: string;
    state: string;
    city: string;
    eulas: Eula[];
    language: string;
    creatorCode?: string;
    // deprecated fields below
    foreignTax?: boolean;
    taxId?: string;
    taxType?: string;
    businessType?: string;
}

interface State {
    form: Form;
    showError: boolean;
    channelValidated: boolean;
    allChannelsIsValid: boolean;
    isFormSubmitted: boolean;
}

interface AddSocialRefObject {
    updateFormErrors: () => boolean;
}

class ApplicationForm extends React.Component<ApplicationFormProps & WithTalonProps, State> {
    addSocialRef: React.RefObject<AddSocialRefObject>;

    static defaultProps = {
        disclaimerEnabled: true
    };

    constructor(props) {
        super(props);
        this.state = {
            showError: false,
            channelValidated: true,
            allChannelsIsValid: true,
            form: autoFillForm(props),
            isFormSubmitted: false
        };

        this.addSocialRef = React.createRef();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const {influencerState} = nextProps;
        const {disconnectedPlatform, severConnection = false} = influencerState || {};

        if (disconnectedPlatform) {
            const {form} = this.state;
            const {channels = []} = form;
            const foundChannelIndex = channels.findIndex(
                ch => ch.platform === disconnectedPlatform
            );
            const channel = channels[foundChannelIndex];

            if (!severConnection) {
                channels.splice(foundChannelIndex, 1);
            } else {
                const {oauthDetails = {}} = channel || {};
                Object.assign(channels[foundChannelIndex], {
                    channelStatus: 'NONE',
                    mainChannel: false,
                    oauthDetails: {
                        ...oauthDetails,
                        valid: false
                    }
                });
            }

            // reset main channel
            let mainChannelPlatform = form.mainChannelPlatform;
            if (!channels.length || form.mainChannelPlatform === disconnectedPlatform) {
                mainChannelPlatform = null;
            }

            // auto-select new main channel
            if (!mainChannelPlatform) {
                const foundChannelIndex = channels.findIndex(isValidChannel);
                if (channels[foundChannelIndex]) {
                    channels[foundChannelIndex].mainChannel = true;
                    mainChannelPlatform = channels[foundChannelIndex].platform;
                }
            }

            this.updateForm({channels, mainChannelPlatform});
            this.props.resetDisconnectedPlatform(disconnectedPlatform);
        }
    }

    updateFormErrors = (hasError = false) => {
        const {form} = this.state;
        let error = hasError;

        // Guardian must accept guardian agreement checkbox if not 18
        if (!form.age18Acknowledgment && !form.guardianAcknowledgment) {
            error = true;
        }

        // All agreements must be accepted prior to submission
        if (!form.agreementAccepted || !form.eulaAccepted) {
            error = true;
        }

        if (!form.mainChannelPlatform) {
            error = true;
        }

        this.setState({
            showError: error
        });

        return error;
    };

    updateForm = data => {
        const form = {...this.state.form, ...data};
        this.setState({form});
    };

    updateSocialFormErrors = () => {
        const {current: addSocialRef} = this.addSocialRef || {};
        if (!addSocialRef) return true;
        return addSocialRef.updateFormErrors();
    };

    submitForm = () => {
        // when submitting form we need to verify all form data
        // however, SocialChannelSection  is split and error message won't display unless
        // we call child component (i.e. SocialChannelSection.updateFormErrors)
        // to-do: refactor error handling so we don't have to use this.addSocial ref
        if (this.updateSocialFormErrors()) return;

        this.setState({
            isFormSubmitted: true
        });

        this.props.talon.initCaptcha(solveToken => {
            if (this.updateSocialFormErrors()) return;
            this.props.createInfluencerProfile({...this.state.form, solveToken});
        });
    };

    updateEulaStatus = (formKey, checked) => {
        const form = {...this.state.form};

        // fetch eula from state key
        const map = {agreementAccepted: 'affiliate', eulaAccepted: 'conduct'};
        const eula = this.props.eulasState[map[formKey]];
        if (eula) {
            if (!checked) {
                form.eulas = form.eulas.filter(_eula => _eula.key !== eula.key);
            } else {
                form.eulas.push({
                    key: eula.key,
                    version: eula.version,
                    locale: eula.locale
                });
            }
        }

        form[formKey] = checked;
        this.updateForm(form);
    };

    trigger = (name, value) => {
        const {form} = this.state;
        switch (name) {
            case 'ageAcknowledgment':
                form.age18Acknowledgment = !!value;
                form.guardianAcknowledgment = false;
                form.guardianAccepted = false;
                break;
            case 'guardianAcknowledgment':
                form.guardianAcknowledgment = !!value;
                form.age18Acknowledgment = false;
                break;
            case 'foreignBank':
                set(form, name, value === 'true');
                break;
            case 'guardianFirstName':
                form.guardianInfo.firstName = value;
                break;
            case 'guardianLastName':
                form.guardianInfo.lastName = value;
                break;
            case 'guardianRelationship':
                form.guardianInfo.relationship = value;
                break;
            case 'foreignTax':
                form.foreignTax = value === 'true';
                form.country = form.foreignTax ? Countries.slice(1)[0].value : 'US';
                break;
            case 'businessType':
                if (!form.foreignTax) {
                    form.taxType = form.businessType === 'INDIVIDUAL' ? 'SSN' : 'EIN';
                }
                break;
            case 'taxType':
                form.taxId = '';
                break;
            case 'country':
                form.state = '';
                form.country = value;
                break;
            case 'mainChannelPlatform':
                form.mainChannelPlatform = value;
                form.channels = form.channels.map(ch => {
                    ch.mainChannel = ch.platform === value;
                    return ch;
                });
                break;
            default:
                set(form, name, value);
                break;
        }

        this.updateForm(form);
    };

    renderValidatedFormError = () => {
        const {isFormSubmitted} = this.state;
        const {affiliateAccount, loading, createAffiliateError} = this.props.influencerState;
        if (isFormSubmitted && !loading) {
            if (createAffiliateError) {
                return (
                    <div className={styles.errorMessage}>
                        <Message code={ErrorCode.SOCIAL_CHANNEL_CREATE_AFFILIATE_ERROR} />
                    </div>
                );
            }

            if (!affiliateAccount) {
                return (
                    <div className={styles.errorMessage}>
                        <Message code={ErrorCode.SOCIAL_CHANNEL_SOMETHING_WRONG} />
                    </div>
                );
            }
        }

        if (!this.state.showError) return null;

        return (
            <div className={styles.errorMessage}>
                <Message code={'epic.affiliate.signup.form.fill_required_fields'} />
            </div>
        );
    };

    renderFormContent() {
        const {isEulasLoading, talon, children, ...rest} = this.props;
        const {form, showError} = this.state;
        const {channels = []} = form;

        const isLoading =
            talon.talonState.loading ||
            this.props.isEulasLoading ||
            this.props.userState.loading ||
            this.props.influencerState.loading;

        // only enable the submit button once everything is loaded
        // and agreements are accepted
        let enableSubmit =
            !isLoading &&
            !isEulasLoading &&
            form.eulaAccepted &&
            form.agreementAccepted &&
            !(!form.age18Acknowledgment && !form.guardianAccepted) &&
            isValidSocialChannels(channels);

        if (this.props.disclaimerEnabled && !form.disclaimerAccepted) {
            enableSubmit = false;
        }

        const ChildComponent = children;

        return (
            <ChildComponent
                ref={{
                    addSocialRef: this.addSocialRef
                }}
                form={form}
                styles={styles}
                getMessage={getMessage}
                enableSubmit={enableSubmit}
                updateForm={this.updateForm}
                trigger={this.trigger}
                updateFormErrors={this.updateFormErrors}
                updateEulaStatus={this.updateEulaStatus}
                renderValidatedFormError={this.renderValidatedFormError}
                showError={showError}
                {...rest}
            />
        );
    }

    render() {
        const {influencerState, messages, userState, talon} = this.props;
        const isLoading =
            talon.talonState.loading ||
            this.props.isEulasLoading ||
            userState.loading ||
            influencerState.loading;

        return (
            <div className={styles.content}>
                <div className={styles.container}>
                    <FormsyForm
                        invalidSubmit={this.updateFormErrors}
                        submitForm={this.submitForm}
                        callbackDelay={2000}>
                        <LoadIndicator
                            className={styles.sections}
                            isLoading={isLoading}
                            messages={messages}>
                            {this.renderFormContent()}
                        </LoadIndicator>
                    </FormsyForm>
                </div>
            </div>
        );
    }
}

export default withTalon(ApplicationForm);
